/**
 * Lines up two recordings of the same event by their sound.
 *
 * Each recording becomes a loudness envelope (log energy every 10 ms, with
 * the slow level removed), which looks the same whether it came from a lapel
 * mic or a camera across the room. The envelopes are cross-correlated with an
 * FFT to find the coarse offset, then the offset is refined to about a
 * millisecond with a finer envelope around that point. The match is scored by
 * how much the best alignment stands out from the next best one, so silence
 * or unrelated recordings come back as "weak" instead of a confident guess.
 */

import type { PcmClip } from './audioDecode';

export type SyncQuality = 'strong' | 'fair' | 'weak';

export interface SyncEstimate {
  /** masterTime = angleTime + offsetS (presentation seconds of each file). */
  offsetS: number;
  quality: SyncQuality;
  /** Normalized correlation at the best alignment (0..1). */
  score: number;
  /** How far the best alignment stands out from the next best (≥ 1). */
  distinctness: number;
  /** Seconds the two clips overlap at that alignment. */
  overlapS: number;
}

// ---------------------------------------------------------------------------
// FFT
// ---------------------------------------------------------------------------

const nextPowerOfTwo = (value: number) => {
  let size = 1;
  while (size < value) size *= 2;
  return size;
};

/** In-place iterative radix-2 FFT; `inverse` skips the 1/n scaling. */
export const fft = (re: Float64Array, im: Float64Array, inverse = false) => {
  const n = re.length;
  for (let i = 1, j = 0; i < n; i += 1) {
    let bit = n >> 1;
    for (; j & bit; bit >>= 1) j ^= bit;
    j ^= bit;
    if (i < j) {
      let swap = re[i];
      re[i] = re[j];
      re[j] = swap;
      swap = im[i];
      im[i] = im[j];
      im[j] = swap;
    }
  }
  for (let size = 2; size <= n; size *= 2) {
    const angle = ((inverse ? 2 : -2) * Math.PI) / size;
    const stepRe = Math.cos(angle);
    const stepIm = Math.sin(angle);
    const half = size / 2;
    for (let start = 0; start < n; start += size) {
      let wRe = 1;
      let wIm = 0;
      for (let k = 0; k < half; k += 1) {
        const a = start + k;
        const b = a + half;
        const tRe = re[b] * wRe - im[b] * wIm;
        const tIm = re[b] * wIm + im[b] * wRe;
        re[b] = re[a] - tRe;
        im[b] = im[a] - tIm;
        re[a] += tRe;
        im[a] += tIm;
        const nextRe = wRe * stepRe - wIm * stepIm;
        wIm = wRe * stepIm + wIm * stepRe;
        wRe = nextRe;
      }
    }
  }
};

/**
 * Raw cross-correlation c[lag] = Σ m[i + lag] · a[i] for every lag in
 * [-(a.length - 1), m.length - 1]; index lag + a.length - 1.
 */
export const crossCorrelate = (m: Float32Array, a: Float32Array): Float64Array => {
  const size = nextPowerOfTwo(m.length + a.length - 1);
  const mRe = new Float64Array(size);
  const mIm = new Float64Array(size);
  const aRe = new Float64Array(size);
  const aIm = new Float64Array(size);
  for (let i = 0; i < m.length; i += 1) mRe[i] = m[i];
  for (let i = 0; i < a.length; i += 1) aRe[i] = a[i];
  fft(mRe, mIm);
  fft(aRe, aIm);
  // M · conj(A)
  for (let i = 0; i < size; i += 1) {
    const re = mRe[i] * aRe[i] + mIm[i] * aIm[i];
    const im = mIm[i] * aRe[i] - mRe[i] * aIm[i];
    mRe[i] = re;
    mIm[i] = im;
  }
  fft(mRe, mIm, true);
  const out = new Float64Array(m.length + a.length - 1);
  for (let lag = -(a.length - 1); lag < m.length; lag += 1) {
    const index = lag >= 0 ? lag : size + lag;
    out[lag + a.length - 1] = mRe[index] / size;
  }
  return out;
};

// ---------------------------------------------------------------------------
// Envelopes
// ---------------------------------------------------------------------------

/**
 * Log10 of the mean power in each window of `windowS`, one per `hopS` from
 * the clip's first sample, after a gentle high-pass (pre-emphasis) that keeps
 * hum and rumble out. Digital silence comes out at -10 (1e-10).
 */
export const logEnergyFrames = (clip: PcmClip, hopS: number, windowS = hopS): Float32Array => {
  const { samples, sampleRate } = clip;
  const hop = Math.max(1, Math.round(hopS * sampleRate));
  const window = Math.max(hop, Math.round(windowS * sampleRate));
  const frames = Math.max(0, Math.floor((samples.length - window) / hop) + 1);
  const energy = new Float32Array(frames);
  let previous = 0;
  const emphasized = new Float32Array(samples.length);
  for (let i = 0; i < samples.length; i += 1) {
    emphasized[i] = samples[i] - 0.97 * previous;
    previous = samples[i];
  }
  // Running sum of squares for O(1) windows.
  const squares = new Float64Array(samples.length + 1);
  for (let i = 0; i < samples.length; i += 1) squares[i + 1] = squares[i] + emphasized[i] * emphasized[i];
  for (let f = 0; f < frames; f += 1) {
    const start = f * hop;
    energy[f] = Math.log10((squares[start + window] - squares[start]) / window + 1e-10);
  }
  return energy;
};

/**
 * Log energy per hop (see logEnergyFrames), with the local average over
 * `detrendS` subtracted so a quiet camera and a loud lapel mic give the same
 * shape. Silence is floored so room noise doesn't become pattern.
 */
export const loudnessEnvelope = (
  clip: PcmClip,
  hopS = 0.01,
  windowS = hopS * 2,
  detrendS = 1
): Float32Array => {
  const energy = logEnergyFrames(clip, hopS, windowS);
  const frames = energy.length;
  if (frames === 0) return energy;

  // Floor at a little above the quietest tenth (room tone).
  const sorted = Array.from(energy).sort((x, y) => x - y);
  const floor = sorted[Math.floor(sorted.length * 0.1)] + 0.5;
  for (let f = 0; f < frames; f += 1) energy[f] = Math.max(energy[f], floor);

  // Subtract the local mean.
  const radius = Math.max(1, Math.round(detrendS / hopS / 2));
  const prefix = new Float64Array(frames + 1);
  for (let f = 0; f < frames; f += 1) prefix[f + 1] = prefix[f] + energy[f];
  const out = new Float32Array(frames);
  for (let f = 0; f < frames; f += 1) {
    const lo = Math.max(0, f - radius);
    const hi = Math.min(frames, f + radius + 1);
    out[f] = energy[f] - (prefix[hi] - prefix[lo]) / (hi - lo);
  }
  return out;
};

// ---------------------------------------------------------------------------
// Matching
// ---------------------------------------------------------------------------

interface LagScore {
  lag: number;
  score: number;
  overlap: number;
}

/**
 * Normalized correlation for every lag with at least `minOverlap` frames in
 * common: Σ m·a over the overlap divided by the overlap's energies, so a lag
 * that only overlaps a little can't win on size alone.
 */
const normalizedScores = (m: Float32Array, a: Float32Array, minOverlap: number): LagScore[] => {
  const raw = crossCorrelate(m, a);
  const mSq = new Float64Array(m.length + 1);
  const aSq = new Float64Array(a.length + 1);
  for (let i = 0; i < m.length; i += 1) mSq[i + 1] = mSq[i] + m[i] * m[i];
  for (let i = 0; i < a.length; i += 1) aSq[i + 1] = aSq[i] + a[i] * a[i];
  const scores: LagScore[] = [];
  for (let lag = -(a.length - 1); lag < m.length; lag += 1) {
    // a[i] lines up with m[i + lag] for i in [aStart, aEnd).
    const aStart = Math.max(0, -lag);
    const aEnd = Math.min(a.length, m.length - lag);
    const overlap = aEnd - aStart;
    if (overlap < minOverlap) continue;
    const eA = aSq[aEnd] - aSq[aStart];
    const eM = mSq[aEnd + lag] - mSq[aStart + lag];
    if (eA <= 0 || eM <= 0) continue;
    scores.push({ lag, score: raw[lag + a.length - 1] / Math.sqrt(eA * eM), overlap });
  }
  return scores;
};

/** Sub-step peak position from three neighbouring scores. */
const parabolicOffset = (left: number, center: number, right: number) => {
  const denominator = left - 2 * center + right;
  if (denominator === 0) return 0;
  const offset = (0.5 * (left - right)) / denominator;
  return Math.max(-0.5, Math.min(0.5, offset));
};

export interface EstimateOptions {
  /** Shortest overlap worth trusting, in seconds. */
  minOverlapS?: number;
}

const STRONG = { score: 0.35, distinctness: 1.5 };
const FAIR = { score: 0.2, distinctness: 1.25 };

/**
 * The offset that lines `angle` up with `master`, or null when either clip
 * has too little sound to compare.
 */
export const estimateOffset = (master: PcmClip, angle: PcmClip, options: EstimateOptions = {}): SyncEstimate | null => {
  const hopS = 0.01;
  const minOverlapS = options.minOverlapS ?? 20;
  const m = loudnessEnvelope(master, hopS);
  const a = loudnessEnvelope(angle, hopS);
  if (m.length < 100 || a.length < 100) return null;
  const minOverlap = Math.min(Math.round(minOverlapS / hopS), Math.floor(Math.min(m.length, a.length) * 0.8));
  const scores = normalizedScores(m, a, minOverlap);
  if (scores.length === 0) return null;

  let best = 0;
  for (let i = 1; i < scores.length; i += 1) if (scores[i].score > scores[best].score) best = i;
  const peak = scores[best];
  if (!(peak.score > 0)) return null;
  // The next best alignment at least 0.5 s away.
  const exclusion = Math.round(0.5 / hopS);
  let second = 0;
  for (let i = 0; i < scores.length; i += 1) {
    if (Math.abs(scores[i].lag - peak.lag) > exclusion && scores[i].score > second) second = scores[i].score;
  }
  const distinctness = second > 0 ? peak.score / second : 10;

  // Refine around the coarse peak: by phase first (sample-accurate), then by
  // a finer envelope when the phase peak isn't clear.
  const coarseS = master.startS - angle.startS + peak.lag * hopS;
  const refinedS = refineByPhase(master, angle, coarseS) ?? refineOffset(master, angle, coarseS) ?? coarseS;

  const quality: SyncQuality =
    peak.score >= STRONG.score && distinctness >= STRONG.distinctness
      ? 'strong'
      : peak.score >= FAIR.score && distinctness >= FAIR.distinctness
        ? 'fair'
        : 'weak';
  return {
    offsetS: refinedS,
    quality,
    score: peak.score,
    distinctness,
    overlapS: peak.overlap * hopS,
  };
};

/** A stretch of a clip by time; the start is where the copy really begins. */
const cutClip = (clip: PcmClip, fromS: number, lengthS: number): PcmClip => {
  const rate = clip.sampleRate;
  const first = Math.max(0, Math.round((fromS - clip.startS) * rate));
  const last = Math.min(clip.samples.length, first + Math.max(0, Math.round(lengthS * rate)));
  return { samples: clip.samples.subarray(first, last), sampleRate: rate, startS: clip.startS + first / rate };
};

/** The stretch both clips cover at offset `offsetS`, in master time, or null when shorter than `minS`. */
const sharedStretch = (master: PcmClip, angle: PcmClip, offsetS: number, minS: number): [number, number] | null => {
  const rate = master.sampleRate;
  const start = Math.max(master.startS, angle.startS + offsetS);
  const end = Math.min(master.startS + master.samples.length / rate, angle.startS + offsetS + angle.samples.length / rate);
  return end - start >= minS ? [start, end] : null;
};

/**
 * Generalized cross-correlation with phase transform (GCC-PHAT) within ±20 ms
 * of `coarseS`, over 30 s from the middle of the shared stretch. Whitening
 * the spectrum makes the peak sharp even when the two mics colour the sound
 * differently. Speech bands only (80 Hz – 3.5 kHz). Returns null when the
 * peak doesn't stand clear of the rest of the search window.
 */
export const refineByPhase = (master: PcmClip, angle: PcmClip, coarseS: number): number | null => {
  const rate = master.sampleRate;
  if (angle.sampleRate !== rate) return null;
  const searchS = 0.02;
  const shared = sharedStretch(master, angle, coarseS, 4);
  if (!shared) return null;
  const spanS = Math.min(30, shared[1] - shared[0] - 2 * searchS);
  if (spanS < 3) return null;
  const startS = shared[0] + searchS + (shared[1] - shared[0] - 2 * searchS - spanS) / 2;
  const mClip = cutClip(master, startS - searchS, spanS + 2 * searchS);
  const aClip = cutClip(angle, startS - coarseS, spanS);
  const m = mClip.samples;
  const a = aClip.samples;
  if (a.length < rate || m.length <= a.length) return null;

  const size = nextPowerOfTwo(m.length + a.length);
  const mRe = new Float64Array(size);
  const mIm = new Float64Array(size);
  const aRe = new Float64Array(size);
  const aIm = new Float64Array(size);
  for (let i = 0; i < m.length; i += 1) mRe[i] = m[i];
  for (let i = 0; i < a.length; i += 1) aRe[i] = a[i];
  fft(mRe, mIm);
  fft(aRe, aIm);
  const lowBin = Math.ceil((80 / rate) * size);
  const highBin = Math.floor((Math.min(3500, rate / 2 - 1) / rate) * size);
  for (let i = 0; i < size; i += 1) {
    const bin = i <= size / 2 ? i : size - i;
    const re = mRe[i] * aRe[i] + mIm[i] * aIm[i];
    const im = mIm[i] * aRe[i] - mRe[i] * aIm[i];
    const magnitude = Math.sqrt(re * re + im * im);
    if (bin < lowBin || bin > highBin || magnitude < 1e-12) {
      mRe[i] = 0;
      mIm[i] = 0;
    } else {
      mRe[i] = re / magnitude;
      mIm[i] = im / magnitude;
    }
  }
  fft(mRe, mIm, true);

  // a[0] lines up with m[k]: k in [0, 2 · search] covers coarse ± search.
  const maxShift = Math.min(m.length - a.length, Math.round(2 * searchS * rate));
  let best = 0;
  for (let k = 1; k <= maxShift; k += 1) if (mRe[k] > mRe[best]) best = k;
  if (best === 0 || best === maxShift) return null;
  let sumSquares = 0;
  for (let k = 0; k <= maxShift; k += 1) sumSquares += mRe[k] * mRe[k];
  const rms = Math.sqrt(sumSquares / (maxShift + 1));
  if (!(mRe[best] > 4 * rms)) return null;
  const fine = best + parabolicOffset(mRe[best - 1], mRe[best], mRe[best + 1]);
  return mClip.startS + fine / rate - aClip.startS;
};

/**
 * Searches ±20 ms around `coarseS` with a 1 ms envelope over the busiest
 * stretch both clips share (up to 60 s), for a millisecond-level offset.
 */
export const refineOffset = (master: PcmClip, angle: PcmClip, coarseS: number): number | null => {
  if (angle.sampleRate !== master.sampleRate) return null;
  const hopS = 0.001;
  const searchS = 0.02;
  const shared = sharedStretch(master, angle, coarseS, 4);
  if (!shared) return null;
  const spanS = Math.min(60, shared[1] - shared[0] - 2 * searchS);
  const startS = shared[0] + searchS + Math.max(0, (shared[1] - shared[0] - 2 * searchS - spanS) / 2);
  const mClip = cutClip(master, startS - searchS, spanS + 2 * searchS);
  const aClip = cutClip(angle, startS - coarseS, spanS);
  const m = loudnessEnvelope(mClip, hopS, 0.01, 0.2);
  const a = loudnessEnvelope(aClip, hopS, 0.01, 0.2);
  if (a.length < 100 || m.length < a.length) return null;

  const steps = Math.round(searchS / hopS);
  const values: number[] = [];
  let aEnergy = 0;
  for (let i = 0; i < a.length; i += 1) aEnergy += a[i] * a[i];
  for (let shift = 0; shift <= 2 * steps && shift + a.length <= m.length; shift += 1) {
    let sum = 0;
    let mEnergy = 0;
    for (let i = 0; i < a.length; i += 1) {
      const value = m[shift + i];
      sum += value * a[i];
      mEnergy += value * value;
    }
    values.push(aEnergy > 0 && mEnergy > 0 ? sum / Math.sqrt(aEnergy * mEnergy) : 0);
  }
  if (values.length < 3) return null;
  let best = 0;
  for (let i = 1; i < values.length; i += 1) if (values[i] > values[best]) best = i;
  // A peak on the edge of the search means the coarse estimate was off; keep it.
  if (best === 0 || best === values.length - 1) return null;
  const fine = best + parabolicOffset(values[best - 1], values[best], values[best + 1]);
  // a[0] (angle time aClip.startS) lines up with m[fine] (master time mClip.startS + fine · hop).
  return mClip.startS + fine * hopS - aClip.startS;
};

// ---------------------------------------------------------------------------
// From an offset to a set's angle timing
// ---------------------------------------------------------------------------

export interface AngleTiming {
  timelineStartMs: number;
  sourceInMs: number;
}

/**
 * The timeline start and trim that put an angle at `offsetS` from the master.
 *
 * Timeline time t shows master time t + masterSourceIn and angle time
 * t − timelineStart + sourceIn, so the two line up when
 * timelineStart − sourceIn = offset − masterSourceIn. An existing trim is kept
 * when it can be; an angle that started before the master is trimmed by the
 * difference instead, since nothing may start before the timeline.
 */
export const timingForOffset = (offsetS: number, masterSourceInMs: number, currentSourceInMs: number): AngleTiming => {
  const differenceMs = Math.round(offsetS * 1000) - Math.round(masterSourceInMs);
  const keptStart = differenceMs + Math.max(0, Math.round(currentSourceInMs));
  if (keptStart >= 0) return { timelineStartMs: keptStart, sourceInMs: Math.max(0, Math.round(currentSourceInMs)) };
  return { timelineStartMs: 0, sourceInMs: -differenceMs };
};
